import * as signalR from '@microsoft/signalr';
import { API_URL } from '@/http';
import { auth } from '@/auth';
import { connectWithBackoff } from './connectWithBackoff';
import { IBackgroundJobChangedEvent } from '@models/backgroundJobs/IBackgroundJob';

type NotificationCountCallback = (unreadCount?: number) => void;

/**
 * Запрос поддержки к пользователю (IMPLEMENTATION.md §15): приглашение к показу экрана или согласие на вход. Приходит
 * при создании и на каждую смену статуса; список ожидающих — `GET supportSessions/requests`.
 */
export interface ISupportRequestEvent {
    id: string;
    kind: 'invite' | 'consent';
    status?: 'pending' | 'accepted' | 'declined' | 'expired' | 'cancelled';
}
type SupportRequestCallback = (event: ISupportRequestEvent) => void;
type JobChangedCallback = (event: IBackgroundJobChangedEvent) => void;

class NotificationHub {
    private connection: signalR.HubConnection;
    private newCallbacks: NotificationCountCallback[] = [];
    private changedCallbacks: NotificationCountCallback[] = [];
    /** Подписчики живут вне шапки (плашки запросов) — `stop()` их не сбрасывает, отписка — их собственная. */
    private supportRequestCallbacks: SupportRequestCallback[] = [];
    /** Как и запросы поддержки, не сбрасываются в `stop()`. */
    private reconnectedCallbacks: (() => void)[] = [];
    /** Клиент фоновых задач переживает перезапуск хаба при смене пользователя — `stop()` их не сбрасывает. */
    private jobChangedCallbacks: JobChangedCallback[] = [];
    private stopPromise: Promise<void> | null = null;
    private stopped = false;

    constructor() {
        const logLevel = import.meta.env.PROD ? signalR.LogLevel.Warning : signalR.LogLevel.Trace;

        this.connection = new signalR.HubConnectionBuilder()
            .configureLogging(logLevel)
            .withUrl(`${API_URL}/hubs/notification`, {
                accessTokenFactory: () => auth.pageAccessToken() ?? '',
            })
            .withAutomaticReconnect()
            .build();

        this.connection.on('NewNotification', (unreadCount?: number) => {
            this.newCallbacks.forEach((cb) => cb(unreadCount));
        });

        this.connection.on('NotificationsChanged', (unreadCount?: number) => {
            this.changedCallbacks.forEach((cb) => cb(unreadCount));
        });

        this.connection.on('SupportRequest', (event: ISupportRequestEvent) => {
            this.supportRequestCallbacks.forEach((cb) => cb(event));
        });

        this.connection.on('JobChanged', (event: IBackgroundJobChangedEvent) => {
            if (!event || typeof event.jobId !== 'string' || typeof event.status !== 'string') return;
            this.jobChangedCallbacks.forEach((cb) => cb(event));
        });

        this.connection.onreconnected(() => this.emitReconnected());
    }

    private emitReconnected(): void {
        this.reconnectedCallbacks.forEach((cb) => cb());
    }

    public async start(): Promise<void> {
        this.stopped = false;
        const wasConnected = this.isConnected;
        await connectWithBackoff(this.connection, 'NotificationHub', {
            isStopped: () => this.stopped,
            consumeStopPromise: () => {
                const p = this.stopPromise;
                this.stopPromise = null;
                return p;
            },
        });
        // Подключение после отказов (или первое) — события, разосланные до него, не дошли. Уже подключённый хаб
        // (повторный start) ничего не пропустил.
        if (!this.stopped && !wasConnected && this.isConnected) this.emitReconnected();
    }

    public stop(): void {
        this.stopped = true;
        this.newCallbacks = [];
        this.changedCallbacks = [];

        if (this.connection.state !== signalR.HubConnectionState.Disconnected) {
            this.stopPromise = this.connection.stop().catch(() => {});
        }
    }

    public get isConnected(): boolean {
        return this.connection.state === signalR.HubConnectionState.Connected;
    }

    public onNewNotification(callback: NotificationCountCallback): () => void {
        this.newCallbacks.push(callback);
        return () => {
            this.newCallbacks = this.newCallbacks.filter((cb) => cb !== callback);
        };
    }

    public onNotificationsChanged(callback: NotificationCountCallback): () => void {
        this.changedCallbacks.push(callback);
        return () => {
            this.changedCallbacks = this.changedCallbacks.filter((cb) => cb !== callback);
        };
    }

    /**
     * Соединение (вновь) установлено: автоматическое переподключение или успешный `start()`. События, разосланные
     * без соединения, потеряны — подписчик перечитывает своё состояние.
     */
    public onReconnected(callback: () => void): () => void {
        this.reconnectedCallbacks.push(callback);
        return () => {
            this.reconnectedCallbacks = this.reconnectedCallbacks.filter((cb) => cb !== callback);
        };
    }

    /** Смена состояния своей фоновой задачи: идентификатор и статус, подробности — `GET jobs/{id}`. */
    public onJobChanged(callback: JobChangedCallback): () => void {
        this.jobChangedCallbacks.push(callback);
        return () => {
            this.jobChangedCallbacks = this.jobChangedCallbacks.filter((cb) => cb !== callback);
        };
    }

    public onSupportRequest(callback: SupportRequestCallback): () => void {
        this.supportRequestCallbacks.push(callback);
        return () => {
            this.supportRequestCallbacks = this.supportRequestCallbacks.filter((cb) => cb !== callback);
        };
    }
}

export const notificationHub = new NotificationHub();
